import { useEffect, useState, useRef } from "react";
import "../App.css";
import "../Backgrounds.css";
import "./Home.css";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import GameAIVisualization from "../components/GameAIVisualization";

// Stage sequences for each path
const PATH_STAGES = {
  attack: ["sense", "evaluate", "attack", "merge", "resolve", "output"],
  defend: ["sense", "evaluate", "defend", "merge", "resolve", "output"],
  flee: ["sense", "evaluate", "flee", "merge", "resolve", "output"],
};

const GameAIDemo = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [activePath, setActivePath] = useState("defend");
  const [loopCount, setLoopCount] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [speed, setSpeed] = useState(800);
  const [maxLoops, setMaxLoops] = useState(2);
  const intervalRef = useRef(null);
  const stageIndexRef = useRef(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-play animation with loopback handling
  useEffect(() => {
    if (autoPlay && isProcessing) {
      const stages = PATH_STAGES[activePath];

      intervalRef.current = setInterval(() => {
        stageIndexRef.current++;

        if (stageIndexRef.current >= stages.length) {
          // Reached end - check if we should loop back
          if (loopCount < maxLoops) {
            // Loopback: resolve → sense
            setLoopCount((prev) => prev + 1);
            stageIndexRef.current = 0;
            setCurrentNode("sense");
          } else {
            // Max loops reached - reset
            stageIndexRef.current = 0;
            setCurrentNode(stages[0]);
            setLoopCount(0);
          }
        } else {
          setCurrentNode(stages[stageIndexRef.current]);
        }
      }, speed);

      return () => clearInterval(intervalRef.current);
    }
  }, [autoPlay, isProcessing, activePath, loopCount, maxLoops, speed]);

  const handleStart = () => {
    const stages = PATH_STAGES[activePath];
    stageIndexRef.current = 0;
    setCurrentNode(stages[0]);
    setIsProcessing(true);
    setLoopCount(0);
  };

  const handleStop = () => {
    setIsProcessing(false);
    setCurrentNode(null);
    setLoopCount(0);
    stageIndexRef.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleNextStage = () => {
    const stages = PATH_STAGES[activePath];
    stageIndexRef.current++;

    if (stageIndexRef.current >= stages.length) {
      if (loopCount < maxLoops) {
        setLoopCount((prev) => prev + 1);
        stageIndexRef.current = 0;
        setCurrentNode("sense");
      } else {
        stageIndexRef.current = 0;
        setCurrentNode(stages[0]);
        setLoopCount(0);
      }
    } else {
      setCurrentNode(stages[stageIndexRef.current]);
    }
  };

  const handleTriggerLoopback = () => {
    // Force a loopback from current position
    if (isProcessing) {
      setLoopCount((prev) => prev + 1);
      stageIndexRef.current = 0;
      setCurrentNode("sense");
    }
  };

  const handleNodeClick = (nodeId) => {
    if (isProcessing) {
      const stages = PATH_STAGES[activePath];
      const index = stages.indexOf(nodeId);
      if (index !== -1) {
        stageIndexRef.current = index;
        setCurrentNode(nodeId);
      }
    }
  };

  return (
    <div>
      <div className="foreground">
        <MainHeader />

        <div className="homeBackground">
          <div className="homeContentContainer">
            <div className="block">
              <h1
                style={{
                  color: "var(--font-colour-content1)",
                  textAlign: "center",
                }}
              >
                Game AI Decision Flow
              </h1>
              <p
                style={{
                  color: "var(--font-colour-content2)",
                  textAlign: "center",
                  marginBottom: "30px",
                }}
              >
                3-way decision split with dual loopback arcs
              </p>

              {/* Visualization */}
              <div
                style={{
                  background: "#030509",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                  marginBottom: "30px",
                  minHeight: "380px",
                }}
              >
                <GameAIVisualization
                  currentNode={currentNode}
                  activePath={activePath}
                  isProcessing={isProcessing}
                  loopCount={loopCount}
                  onNodeClick={handleNodeClick}
                />
              </div>

              {/* Controls */}
              <div
                style={{
                  background: "#0a0c12",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                  padding: "24px",
                }}
              >
                {/* Path Selection - 3-way */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      color: "#6a7080",
                      textTransform: "uppercase",
                      letterSpacing: "0.25em",
                      marginBottom: "12px",
                    }}
                  >
                    AI Decision Path (3-way split)
                  </label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    {["attack", "defend", "flee"].map((path) => (
                      <button
                        key={path}
                        onClick={() => setActivePath(path)}
                        style={{
                          flex: 1,
                          padding: "12px 20px",
                          borderRadius: "8px",
                          border:
                            activePath === path
                              ? `1px solid ${path === "attack" ? "#e54a4a" : path === "defend" ? "#4a9eb5" : "#b5944a"}`
                              : "1px solid #2a2f38",
                          background:
                            activePath === path
                              ? `rgba(${path === "attack" ? "229,74,74" : path === "defend" ? "74,158,181" : "181,148,74"},0.1)`
                              : "transparent",
                          color:
                            activePath === path
                              ? path === "attack"
                                ? "#e54a4a"
                                : path === "defend"
                                  ? "#4a9eb5"
                                  : "#b5944a"
                              : "#6a7080",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                        }}
                      >
                        {path}
                      </button>
                    ))}
                  </div>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#4a5568",
                      marginTop: "8px",
                      textAlign: "center",
                    }}
                  >
                    {activePath === "attack" &&
                      "Aggressive: Direct engagement path"}
                    {activePath === "defend" &&
                      "Cautious: Balanced defensive strategy"}
                    {activePath === "flee" &&
                      "Retreat: Escape with reconsider loopback"}
                  </p>
                </div>

                {/* Loopback Controls */}
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    marginBottom: "24px",
                    padding: "16px",
                    background: "#020407",
                    borderRadius: "8px",
                    border: "1px solid rgba(158,107,74,0.2)",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "10px",
                        color: "#9e6b4a",
                        textTransform: "uppercase",
                        letterSpacing: "0.25em",
                        marginBottom: "8px",
                      }}
                    >
                      Max Loops (Resolve → Sense)
                    </label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {[0, 1, 2, 3].map((num) => (
                        <button
                          key={num}
                          onClick={() => setMaxLoops(num)}
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "6px",
                            border:
                              maxLoops === num
                                ? "1px solid #9e6b4a"
                                : "1px solid #2a2f38",
                            background:
                              maxLoops === num
                                ? "rgba(158,107,74,0.2)"
                                : "transparent",
                            color: maxLoops === num ? "#9e6b4a" : "#6a7080",
                            cursor: "pointer",
                            fontWeight: "600",
                          }}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "10px",
                        color: "#9e6b4a",
                        textTransform: "uppercase",
                        letterSpacing: "0.25em",
                        marginBottom: "8px",
                      }}
                    >
                      Current Loop
                    </label>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "300",
                        color: loopCount > 0 ? "#9e6b4a" : "#2a2f38",
                      }}
                    >
                      {loopCount} / {maxLoops}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <button
                      onClick={handleTriggerLoopback}
                      disabled={!isProcessing}
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #9e6b4a",
                        background: !isProcessing
                          ? "transparent"
                          : "rgba(158,107,74,0.1)",
                        color: !isProcessing ? "#2a2f38" : "#9e6b4a",
                        cursor: !isProcessing ? "not-allowed" : "pointer",
                        fontSize: "11px",
                        fontWeight: "600",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      Trigger Loopback
                    </button>
                  </div>
                </div>

                {/* Speed & Auto Controls */}
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    marginBottom: "24px",
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "10px",
                        color: "#6a7080",
                        textTransform: "uppercase",
                        letterSpacing: "0.25em",
                        marginBottom: "12px",
                      }}
                    >
                      Animation Speed: {speed}ms
                    </label>
                    <input
                      type="range"
                      min="200"
                      max="2000"
                      step="100"
                      value={speed}
                      onChange={(e) => setSpeed(parseInt(e.target.value))}
                      style={{ width: "100%", cursor: "pointer" }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        color: "#6a7080",
                        fontSize: "12px",
                        padding: "8px 0",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={autoPlay}
                        onChange={(e) => setAutoPlay(e.target.checked)}
                      />
                      Auto-advance
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    onClick={handleStart}
                    disabled={isProcessing}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #4a9eb5",
                      background: isProcessing
                        ? "transparent"
                        : "rgba(74,158,181,0.1)",
                      color: isProcessing ? "#2a2f38" : "#4a9eb5",
                      cursor: isProcessing ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Start AI
                  </button>
                  <button
                    onClick={handleNextStage}
                    disabled={!isProcessing || autoPlay}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #6a7080",
                      background: "transparent",
                      color: !isProcessing || autoPlay ? "#2a2f38" : "#6a7080",
                      cursor:
                        !isProcessing || autoPlay ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Next Stage
                  </button>
                  <button
                    onClick={handleStop}
                    disabled={!isProcessing}
                    style={{
                      padding: "12px 24px",
                      borderRadius: "8px",
                      border: "1px solid #9e6b4a",
                      background: "transparent",
                      color: !isProcessing ? "#2a2f38" : "#9e6b4a",
                      cursor: !isProcessing ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Stop
                  </button>
                </div>
              </div>

              {/* Info Section */}
              <div
                style={{
                  marginTop: "30px",
                  padding: "20px",
                  background: "#020407",
                  borderRadius: "12px",
                  border: "1px solid #0f1218",
                }}
              >
                <h3
                  style={{
                    fontSize: "14px",
                    color: "#d9dada",
                    marginBottom: "15px",
                    fontWeight: "500",
                  }}
                >
                  Flow Structure
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        fontSize: "11px",
                        color: "#4a9eb5",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      3-Way Split (Evaluate)
                    </h4>
                    <ul
                      style={{
                        color: "#6a7080",
                        fontSize: "11px",
                        lineHeight: "1.8",
                        paddingLeft: "16px",
                        margin: 0,
                      }}
                    >
                      <li>
                        <span style={{ color: "#e54a4a" }}>Attack</span> -
                        Diagonal up path
                      </li>
                      <li>
                        <span style={{ color: "#4a9eb5" }}>Defend</span> -
                        Horizontal center path
                      </li>
                      <li>
                        <span style={{ color: "#b5944a" }}>Flee</span> -
                        Diagonal down path
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "11px",
                        color: "#9e6b4a",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      Dual Loopbacks
                    </h4>
                    <ul
                      style={{
                        color: "#6a7080",
                        fontSize: "11px",
                        lineHeight: "1.8",
                        paddingLeft: "16px",
                        margin: 0,
                      }}
                    >
                      <li>
                        <strong style={{ color: "#9e6b4a" }}>
                          Flee → Evaluate
                        </strong>{" "}
                        - Reconsider decision
                      </li>
                      <li>
                        <strong style={{ color: "#9e6b4a" }}>
                          Resolve → Sense
                        </strong>{" "}
                        - Full cycle restart
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="solidLine"></div>

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameAIDemo;
